import { useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthBoundary } from './AuthBoundary';
import { AuthLayout } from './auth/AuthLayout';
import { ResetPasswordScreen } from './auth/ResetPasswordScreen';
import { SignInScreen } from './auth/SignInScreen';
import { REMEMBERED_ORG_KEY } from './lib/memberships';
import { isRecoveryEntry } from './lib/password-reset';
import { supabase } from './lib/supabase';
import { putusanSesi } from './lib/session-recovery';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { TrialStartPage } from './pages/TrialStartPage';
import type { AuthState } from './api/request';
import { AppShell } from './app/AppShell';
import { CheckPage } from './pages/CheckPage';
import { CorrectionNewPage } from './pages/CorrectionNewPage';
import { CorrectionQueuePage } from './pages/CorrectionQueuePage';
import { KebijakanAbsensiPage } from './pages/KebijakanAbsensiPage';
import { SetelanTugasPage } from './pages/SetelanTugasPage';
import { PemberitahuanPage } from './pages/PemberitahuanPage';
import { DayDetailPage } from './pages/DayDetailPage';
import { HistoryPage } from './pages/HistoryPage';
import { HomePage } from './pages/HomePage';
import { MenuPage } from './pages/MenuPage';
import { MyTasksPage } from './pages/MyTasksPage';
import { PaketPage } from './pages/PaketPage';
import { TaskAssignPage } from './pages/TaskAssignPage';
import { TaskDetailPage } from './pages/TaskDetailPage';
import { DeliveryBoardPage } from './pages/DeliveryBoardPage';
import { AttendanceDashboardPage } from './pages/AttendanceDashboardPage';
import { MonthlyAttendancePage } from './pages/MonthlyAttendancePage';
import { LaporanPage } from './pages/LaporanPage';
import { FailureReviewPage } from './pages/FailureReviewPage';
import { ProofPage } from './pages/ProofPage';
import { TaskDashboardPage } from './pages/TaskDashboardPage';
import { TaskFailPage } from './pages/TaskFailPage';
import { TaskManagePage } from './pages/TaskManagePage';
import { WorkLocationPage } from './pages/WorkLocationPage';
import { WorkerManagePage } from './pages/WorkerManagePage';
import { AssetsPage } from './pages/AssetsPage';
import { AlertsPage } from './pages/AlertsPage';
import { AlertRulesPage } from './pages/AlertRulesPage';
import { TeamsPage } from './pages/TeamsPage';
import { TaskNewPage } from './pages/TaskNewPage';

/**
 * Aplikasi Workforce.
 *
 * Rute mengikuti UI-PAGE-MAP §2. Slice ini membawa Beranda dan Check In/Out; riwayat, koreksi,
 * tugas lapangan, pengiriman, dan dashboard menyusul pada Issue #463–#467 dan menempel pada
 * kerangka yang sama.
 */
export function App(): ReactNode {
  const [auth, setAuth] = useState<AuthState | null>(null);

  /**
   * Token yang dipotret sekali akan MATI DI TENGAH SHIFT.
   *
   * Access token Supabase berumur pendek (bawaan satu jam) dan disegarkan otomatis di latar.
   * Tetapi `auth.accessToken` di sini adalah salinan; tanpa berlangganan pembaruannya, salinan
   * itu kedaluwarsa dan setiap permintaan berikutnya menjadi 401 — pada kurir yang sedang di
   * jalan, di layar yang tidak menjelaskan apa-apa. Berlangganan membuat salinannya ikut
   * bergerak.
   *
   * `SIGNED_OUT` (atau sesi yang hilang) mengembalikan aplikasi ke layar masuk alih-alih
   * membiarkannya memanggil API dengan token mati.
   */
  useEffect(() => {
    /*
     * Satu percobaan pemulihan per sesi hidup aplikasi. Disimpan di ref, bukan state: nilainya
     * dibaca di dalam callback yang tidak boleh memicu render, dan render ulang tidak boleh
     * mengembalikannya ke "belum pernah mencoba".
     */
    let sudahMencobaPulih = false;

    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      const putusan = putusanSesi(event, session !== null, sudahMencobaPulih);

      if (putusan === 'pertahankan' && session) {
        setAuth((sekarang) => {
          if (sekarang === null) return null;
          if (session.access_token === sekarang.accessToken) return sekarang;
          return { ...sekarang, accessToken: session.access_token };
        });
        sudahMencobaPulih = false; // sesi sehat lagi; jatah percobaan dipulihkan
        return;
      }

      if (putusan === 'keluar') {
        setAuth(null);
        return;
      }

      /*
       * SATU percobaan menyegarkan sebelum menyerah.
       *
       * Sebelum ini, penyegaran yang gagal SESAAT — ponsel baru bangun, jaringan belum siap —
       * langsung berarti layar masuk, dan orangnya harus mengetik kata sandi lagi di tengah
       * shift. Server sendiri tidak pernah mengeluarkan siapa pun (`sessions_timebox: 0`).
       *
       * Kegagalannya TIDAK ditelan: bila percobaan ini pun gagal, keputusannya jatuh ke `keluar`
       * pada kabar berikutnya, dan itu memang jawaban yang benar.
       */
      sudahMencobaPulih = true;
      void supabase.auth
        .refreshSession()
        .then(({ data: hasil }) => {
          if (hasil.session) {
            setAuth((sekarang) =>
              sekarang === null ? null : { ...sekarang, accessToken: hasil.session!.access_token },
            );
          } else {
            setAuth(null);
          }
        })
        .catch(() => setAuth(null));
    });
    return () => data.subscription.unsubscribe();
  }, []);

  /*
   * Menerima undangan berdiri DI LUAR batas autentikasi, dan diperiksa sebelum apa pun.
   *
   * Orang yang membukanya belum punya keanggotaan — menuntutnya masuk lebih dulu adalah lingkaran
   * yang tidak punya jalan keluar.
   */
  /**
   * Keluar SUNGGUHAN — bukan sekadar melupakan state.
   *
   * Sebelumnya tombol Keluar hanya memanggil `setAuth(null)`. Sesi Supabase tetap hidup, dan
   * `SignInScreen` memang dirancang MELANJUTKAN sesi yang masih hidup begitu ia dimuat.
   *
   * Akibatnya: menekan Keluar mengembalikan layar masuk, lalu layar itu langsung melanjutkan ke
   * pemilihan organisasi tanpa menanyakan kata sandi. Orang berikutnya yang memegang perangkat
   * yang sama masuk sebagai orang sebelumnya — dan keduanya percaya sudah keluar.
   */
  async function keluar(): Promise<void> {
    try {
      await supabase.auth.signOut();
    } finally {
      // Dijalankan APA PUN hasilnya: kegagalan jaringan tidak boleh membuat orang tetap masuk
      // pada perangkat yang baru saja ia serahkan.
      localStorage.removeItem(REMEMBERED_ORG_KEY);
      setAuth(null);
    }
  }

  /*
   * Kedatangan dari tautan PEMULIHAN kata sandi — diperiksa SEKALI saat mount, dari URL apa adanya.
   *
   * Tautan pemulihan membentuk sesi Supabase yang sah. Tanpa pemeriksaan ini, layar masuk akan
   * melihat sesi itu dan langsung membawa orangnya ke pemilihan organisasi — melewati satu-satunya
   * hal yang ia datang untuk lakukan, yaitu MENGGANTI kata sandinya.
   *
   * Nilainya tidak dihitung ulang tiap render: `window.location.hash` dibersihkan Supabase setelah
   * sesi terbentuk, jadi pembacaan kedua akan menjawab "bukan pemulihan" dan melempar orangnya
   * keluar dari layar yang sedang ia isi.
   */
  const [recovering, setRecovering] = useState(() =>
    isRecoveryEntry(window.location.search, window.location.hash),
  );

  if (window.location.pathname === '/accept') {
    return (
      <AuthLayout>
        <AcceptInvitePage />
      </AuthLayout>
    );
  }

  /*
   * Trial 14 hari Workforce TERSENDIRI (BLD-015 lanjutan) — berdiri DI LUAR batas autentikasi,
   * pola sama `/accept`. Diperiksa SEBELUM `recovering` di bawah: tautan konfirmasi email signup
   * Supabase bisa membawa parameter yang tumpang tindih secara dangkal dengan heuristik
   * `isRecoveryEntry` (mis. `#error=...` pada tautan kedaluwarsa) — rute berbasis PATHNAME ini
   * menghindari ambiguitas itu sama sekali, `TrialStartPage` menangani deteksi sesinya sendiri.
   */
  if (window.location.pathname === '/trial') {
    return (
      <AuthLayout>
        <TrialStartPage />
      </AuthLayout>
    );
  }

  if (recovering) {
    return (
      <AuthLayout>
        <ResetPasswordScreen
          onDone={() => {
            // URL dibersihkan agar memuat ulang tidak mengembalikan orangnya ke layar ini
            // dengan tautan yang sudah terpakai.
            window.history.replaceState(null, '', window.location.pathname);
            setRecovering(false);
          }}
        />
      </AuthLayout>
    );
  }

  if (auth === null) {
    return (
      <AuthLayout>
        <SignInScreen onSignIn={setAuth} />
      </AuthLayout>
    );
  }

  return (
    <BrowserRouter>
      <AuthBoundary auth={auth} onSignOut={() => void keluar()}>
        {() => (
          <Routes>
            <Route element={<AppShell />}>
              <Route index element={<HomePage />} />
              <Route path="menu" element={<MenuPage />} />
              <Route path="attendance/check" element={<CheckPage />} />
              <Route path="attendance/history" element={<HistoryPage />} />
              <Route path="attendance/history/:workDate" element={<DayDetailPage />} />
              {/*
                Detail hari MILIK ORANG LAIN — layar penyelia (23 Agu 2026).
                Segmennya berbeda jumlah dari rute di atas, jadi keduanya tidak pernah rancu.
                Penegakan izinnya tetap di server: `GET /attendance/:userId/:workDate` memanggil
                `assertCanAccessUser`, sehingga URL yang dikarang berakhir 403, bukan bocor.
              */}
              <Route path="attendance/history/:userId/:workDate" element={<DayDetailPage />} />
              {/* `/new` didaftarkan lebih dulu — bila tidak, ia akan tertangkap rute daftar. */}
              <Route path="attendance/corrections/new" element={<CorrectionNewPage />} />
              <Route path="attendance/corrections" element={<CorrectionQueuePage />} />
              <Route path="pemberitahuan" element={<PemberitahuanPage />} />
              {/* Rute STATIS didaftarkan sebelum `:id`, jika tidak `/tasks/new` tertangkap
                  sebagai id tugas dan detail memuat tugas yang tidak ada. */}
              <Route path="tasks" element={<MyTasksPage />} />
              <Route path="tasks/new" element={<TaskNewPage />} />
              <Route path="tasks/manage" element={<TaskManagePage />} />
              <Route path="attendance-policy" element={<KebijakanAbsensiPage />} />
              <Route path="task-settings" element={<SetelanTugasPage />} />
              <Route path="work-locations" element={<WorkLocationPage />} />
              <Route path="paket" element={<PaketPage />} />
              <Route path="workers" element={<WorkerManagePage />} />
              <Route path="teams" element={<TeamsPage />} />
              <Route path="assets" element={<AssetsPage />} />
              <Route path="alerts" element={<AlertsPage />} />
              <Route path="alert-rules" element={<AlertRulesPage />} />
              <Route path="tasks/:id/assign" element={<TaskAssignPage />} />
              <Route path="tasks/:id/fail" element={<TaskFailPage />} />
              <Route path="tasks/:id/proof" element={<ProofPage />} />
              <Route path="deliveries" element={<DeliveryBoardPage />} />
              {/* Statis sebelum dinamis — `/deliveries/failures` bukan id tugas. */}
              <Route path="deliveries/failures" element={<FailureReviewPage />} />
              <Route path="laporan" element={<LaporanPage />} />
              <Route path="dashboard/attendance" element={<AttendanceDashboardPage />} />
              <Route path="dashboard/attendance/bulanan" element={<MonthlyAttendancePage />} />
              <Route path="dashboard/tasks" element={<TaskDashboardPage />} />
              <Route path="tasks/:id" element={<TaskDetailPage />} />
              {/* Rute tak dikenal kembali ke beranda, bukan layar kosong. */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        )}
      </AuthBoundary>
    </BrowserRouter>
  );
}
